import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AppState, ImageSourcePropType, Platform, View } from 'react-native';
import styled from 'styled-components/native';
import { useFocusEffect } from '@react-navigation/native';

import {
  VideoView,
  useVideoPlayer,
  VideoContentFit,
  VideoPlayer as CustomPlayer
} from 'expo-video';
import * as Audio from 'expo-audio';

import {
  disableOrientationLock,
  enablePortraitOrientationLock
} from '../tools/DeviceHelper';
import { log } from '../tools/LogHelper';

const StyledTouchableOpacity = styled.TouchableOpacity`
  width: 100%;
  height: 100%;
`;

const StyledImage = styled.Image`
  width: 100%;
  height: 100%;
`;

const StyledVideo = styled(VideoView as any)`
  width: 100%;
  height: 100%;
`;

type Props = {
  imageSource?: ImageSourcePropType;
  videoSource: { uri: string };
  disableAutoPlay?: boolean;
  customPlayer?: CustomPlayer;
};

const VideoPlayer: React.FC<Props> = ({
  imageSource,
  videoSource,
  disableAutoPlay,
  customPlayer,
  ...props
}) => {
  const [showThumbnail, setShowThumbnail] = useState(true);
  const [contentFit, setContentFit] = useState<VideoContentFit>('cover');
  const appState = useRef(AppState.currentState);

  const player = customPlayer
    ? customPlayer
    : useVideoPlayer(videoSource, player => {
        if (!disableAutoPlay) {
          player.play();
        }
        player.loop = false;
      });
  // Pause when going into background
  useEffect(() => {
    if (Platform.OS !== 'ios') return;

    const handleAppStateChange = (nextAppState: any) => {
      if (appState.current.match(/inactive|background/)) {
        player.pause();
      }
      appState.current = nextAppState;
    };
    const subscription = AppState.addEventListener(
      'change',
      handleAppStateChange
    );
    return () => subscription.remove();
  }, [player]);

  // Allow audio while device is silent
  useEffect(() => {
    const enableAudio = async () => {
      try {
        await Audio.setAudioModeAsync({
          playsInSilentMode: true
        });
      } catch (e) {
        log('ERROR: ', (e as Error).message);
      }
    };
    enableAudio();
  }, []);

  // Pause video when navigating away
  useFocusEffect(
    useCallback(() => {
      return () => {
        player.pause();
      };
    }, [player])
  );

  useEffect(() => {
    const sub = player.addListener('playingChange', ({ isPlaying }) => {
      if (!isPlaying && player.currentTime >= player.duration) {
        setShowThumbnail(true);
        player.pause();
        player.currentTime = 0;
      }
    });

    return () => sub.remove();
  }, [player]);

  return (
    <View {...props}>
      {imageSource && showThumbnail ? (
        <StyledTouchableOpacity
          activeOpacity={0.8}
          onPress={() => {
            setShowThumbnail(false);
            if (!disableAutoPlay) {
              player.play();
            }
          }}
        >
          <StyledImage source={imageSource} resizeMode='cover' />
        </StyledTouchableOpacity>
      ) : Platform.OS === 'web' ? (
        <video controls autoPlay={!disableAutoPlay} style={{ flex: 1 }}>
          <source src={videoSource?.uri} />
        </video>
      ) : (
        <StyledVideo
          player={player}
          nativeControls
          contentFit={contentFit}
          onFullscreenEnter={() => {
            disableOrientationLock();
            setContentFit('contain');
          }}
          onFullscreenExit={() => {
            enablePortraitOrientationLock();
            setContentFit('cover');
          }}
        />
      )}
    </View>
  );
};

export default VideoPlayer;
